import React, { useMemo, useState } from 'react';
import { showcasePieces, ShowcasePiece, LongformPiece, LinkPiece } from '../../data/showcasePieces';
import ShowcaseModal from './ShowcaseModal';
import LongformTakeover from './LongformTakeover';

const isLongform = (p: ShowcasePiece): p is LongformPiece => p.mediaType === 'longform';
const isLink = (p: ShowcasePiece): p is LinkPiece => p.mediaType === 'link';

interface TileThumbProps {
  piece: ShowcasePiece;
}

/**
 * Tile thumbnail with a per-source fallback chain.
 *
 * For YouTube videos we ask for `maxresdefault.jpg` first (HD when the
 * video has it, real 404 when it doesn't) and fall back to
 * `hqdefault.jpg` on error. `hqdefault.jpg` always returns 200 — even
 * when it serves the gray "no thumbnail" placeholder — so it can't be
 * relied on as the primary, but it's the safest end-of-chain choice
 * since YouTube never 404s it.
 */
const TileThumb: React.FC<TileThumbProps> = ({ piece }) => {
  const sources = useMemo(() => {
    const list = [piece.thumbnail];
    if (piece.mediaType === 'video' && piece.youtubeId) {
      const hq = `https://img.youtube.com/vi/${piece.youtubeId}/hqdefault.jpg`;
      if (!list.includes(hq)) list.push(hq);
    }
    return list;
  }, [piece]);

  const [index, setIndex] = useState(0);
  const src = sources[Math.min(index, sources.length - 1)];

  const badgeLabel =
    piece.mediaType === 'longform'
      ? 'Read'
      : piece.mediaType === 'gallery'
        ? 'Gallery'
        : piece.mediaType === 'video'
          ? 'Watch'
          : piece.mediaType === 'link'
            ? 'Visit'
            : 'View';

  return (
    <div className="showcase-tile-thumb">
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => {
          if (index < sources.length - 1) setIndex(index + 1);
        }}
      />
      <span className={`showcase-tile-badge badge-${piece.mediaType}`}>
        {badgeLabel}
      </span>
    </div>
  );
};

const ShowcaseGrid: React.FC = () => {
  const [activePiece, setActivePiece] = useState<ShowcasePiece | null>(null);

  const handleTileClick = (piece: ShowcasePiece) => {
    setActivePiece(piece);
  };

  const handleClose = () => setActivePiece(null);

  return (
    <div className="showcase-grid-wrapper">
      <header className="showcase-grid-header">
        <h2>Showcase</h2>
        <p>
          Pieces of work that show the craft — campaigns, identities, editorial,
          product moments. Where a piece is part of a larger transformation,
          you can read the case study behind it.
        </p>
      </header>

      <div className="showcase-grid">
        {showcasePieces.map((piece) => {
          const inner = (
            <>
              <TileThumb piece={piece} />
              <div className="showcase-tile-meta">
                <p className="showcase-tile-context">{piece.context}</p>
                <h3 className="showcase-tile-title">{piece.title}</h3>
              </div>
            </>
          );

          return isLink(piece) ? (
            <a
              key={piece.id}
              className={`showcase-tile media-${piece.mediaType}`}
              href={piece.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {inner}
            </a>
          ) : (
            <button
              key={piece.id}
              type="button"
              className={`showcase-tile media-${piece.mediaType}`}
              onClick={() => handleTileClick(piece)}
            >
              {inner}
            </button>
          );
        })}
      </div>

      {activePiece && isLongform(activePiece) && (
        <LongformTakeover piece={activePiece} onClose={handleClose} />
      )}
      {activePiece && !isLongform(activePiece) && !isLink(activePiece) && (
        <ShowcaseModal piece={activePiece} onClose={handleClose} />
      )}
    </div>
  );
};

export default ShowcaseGrid;
